import { useTranslation } from 'react-i18next';
import {
  ArrowDownToLine,
  ArrowUpToLine,
  Copy,
  Eye,
  EyeOff,
  Group,
  Link2Off,
  Lock,
  LockOpen,
  Trash2,
  TriangleAlert,
  Ungroup,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, Select } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import {
  entityTypeFor,
  isLinkable,
  linkStateOf,
  type PlaceableEntity,
  type Scene,
  type SceneElement,
} from '../lib/scene';

interface ElementInspectorProps {
  scene: Scene;
  selection: readonly string[];
  /** Tables and resources belonging to this floor's areas. */
  floorEntities: readonly PlaceableEntity[];
  /**
   * Every entity id the tenant has, on any floor. A link is only *dangling*
   * when the entity is gone altogether — an entity that merely lives on another
   * floor used to raise the same alarm.
   */
  knownEntityIds: ReadonlySet<string>;
  onPatch: (patch: Partial<SceneElement>) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onGroup: () => void;
  onUngroup: () => void;
  onToggleLock: () => void;
  onToggleVisible: () => void;
  onBringToFront: () => void;
  onSendToBack: () => void;
}

/** Numbers commit on blur or Enter, so typing "120" is one undo step, not three. */
function NumberField({
  label,
  value,
  onCommit,
  step = 1,
  disabled,
}: {
  label: string;
  value: number;
  onCommit: (next: number) => void;
  step?: number;
  disabled?: boolean;
}) {
  const rounded = Math.round(value * 10) / 10;
  return (
    <Field label={label}>
      <Input
        key={`${label}:${rounded}`}
        type="number"
        step={step}
        defaultValue={rounded}
        disabled={disabled}
        className="h-9"
        onBlur={(event) => {
          const next = Number(event.currentTarget.value);
          if (Number.isFinite(next) && next !== rounded) onCommit(next);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
      />
    </Field>
  );
}

export function ElementInspector({
  scene,
  selection,
  floorEntities,
  knownEntityIds,
  onPatch,
  onDuplicate,
  onDelete,
  onGroup,
  onUngroup,
  onToggleLock,
  onToggleVisible,
  onBringToFront,
  onSendToBack,
}: ElementInspectorProps) {
  const { t } = useTranslation();
  const chosen = scene.elements.filter((element) => selection.includes(element.id));
  const single = chosen.length === 1 ? chosen[0] : null;
  const grouped = chosen.some((element) => element.groupId);

  if (chosen.length === 0) {
    return (
      <p className="py-6 text-center text-xs text-ink-muted">
        {t('spaces.editor.noSelection')}
      </p>
    );
  }

  const wanted = single?.elementType === 'TABLE' ? 'VenueTable' : 'Resource';
  const targets = floorEntities.filter((entity) => entity.kind === wanted);
  const takenIds = new Set(
    scene.elements
      .filter((element) => element.entityId && element.id !== single?.id)
      .map((element) => element.entityId as string),
  );
  const linkState = single ? linkStateOf(single, knownEntityIds) : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="brand">
          {single ? t(`spaces.element.${single.elementType}`) : t('spaces.editor.selected', { count: chosen.length })}
        </Badge>
        {single?.locked && <Badge tone="neutral">{t('spaces.editor.locked')}</Badge>}
        {single && !single.visible && <Badge tone="neutral">{t('spaces.editor.hidden')}</Badge>}
      </div>

      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant="outline" onClick={onDuplicate} title={t('spaces.editor.duplicate')}>
          <Copy className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={onGroup} disabled={chosen.length < 2} title={t('spaces.editor.group')}>
          <Group className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={onUngroup} disabled={!grouped} title={t('spaces.editor.ungroup')}>
          <Ungroup className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={onToggleLock} title={t('spaces.editor.lock')}>
          {chosen.every((element) => element.locked) ? (
            <LockOpen className="h-4 w-4" />
          ) : (
            <Lock className="h-4 w-4" />
          )}
        </Button>
        <Button size="sm" variant="outline" onClick={onToggleVisible} title={t('spaces.editor.hide')}>
          {chosen.every((element) => !element.visible) ? (
            <Eye className="h-4 w-4" />
          ) : (
            <EyeOff className="h-4 w-4" />
          )}
        </Button>
        <Button size="sm" variant="outline" onClick={onBringToFront} title={t('spaces.editor.bringToFront')}>
          <ArrowUpToLine className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={onSendToBack} title={t('spaces.editor.sendToBack')}>
          <ArrowDownToLine className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="danger" onClick={onDelete} title={t('common.delete')}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {single && (
        <>
          <Field label={t('spaces.editor.label')}>
            <Input
              key={`label:${single.id}:${single.label ?? ''}`}
              defaultValue={single.label ?? ''}
              className="h-9"
              onBlur={(event) => {
                const next = event.currentTarget.value.trim();
                if (next !== (single.label ?? '')) onPatch({ label: next || null });
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur();
              }}
            />
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <NumberField label={t('spaces.editor.x')} value={single.x} onCommit={(x) => onPatch({ x })} />
            <NumberField label={t('spaces.editor.y')} value={single.y} onCommit={(y) => onPatch({ y })} />
            <NumberField
              label={t('spaces.editor.width')}
              value={single.width}
              onCommit={(width) => onPatch({ width: Math.max(8, width) })}
            />
            <NumberField
              label={t('spaces.editor.height')}
              value={single.height}
              onCommit={(height) => onPatch({ height: Math.max(8, height) })}
            />
            <NumberField
              label={t('spaces.editor.rotation')}
              value={single.rotation}
              step={15}
              onCommit={(rotation) => onPatch({ rotation })}
            />
            <NumberField
              label={t('spaces.editor.zIndex')}
              value={single.zIndex}
              onCommit={(zIndex) => onPatch({ zIndex: Math.round(zIndex) })}
            />
          </div>

          {isLinkable(single.elementType) && (
            <Field label={t('spaces.editor.link')} hint={t('spaces.editor.linkHint')}>
              <Select
                className="h-9"
                value={single.entityId ?? ''}
                onChange={(event) => {
                  const entityId = event.currentTarget.value || null;
                  onPatch({
                    entityId,
                    entityType: entityId ? entityTypeFor(single.elementType) : null,
                    // Linking hands the caption back to the entity, so a table
                    // renamed in setup renames itself here too. Unlinking keeps
                    // whatever caption was typed — it is all the element has left.
                    label: entityId ? null : single.label,
                  });
                }}
              >
                <option value="">{t('spaces.editor.linkNone')}</option>
                {targets.map((target) => (
                  <option key={target.id} value={target.id} disabled={takenIds.has(target.id)}>
                    {target.areaName ? `${target.areaName} · ${target.label}` : target.label}
                    {takenIds.has(target.id) ? ` (${t('spaces.editor.alreadyPlaced')})` : ''}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {linkState === 'unlinked' && (
            <p className="flex items-start gap-1.5 text-xs text-amber-600">
              <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {t('spaces.editor.unlinkedWarning')}
            </p>
          )}

          {linkState === 'dangling' && (
            <p className="flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400">
              <Link2Off className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {t('spaces.editor.danglingLink')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
